import type { ChainStepRow, RequirementRow } from '@api/replenishment-engine';
import { Badge, Button, Group, List, Stack, Tabs, Text } from '@mantine/core';
import { formatCount } from '@utils/currency';
import { useEffect, useMemo, useState, type FC, type ReactElement } from 'react';

import ExplainWindow from '../ExplainWindow';
import { addDaysKey, dayLabel, localDateKey, whenLabel } from './dates';
import { groupDips, tiersLabel, unlockLabel, waitsOnLabel, type Dip } from './dips';
import classes from './engine.module.css';
import { ApproveGate } from './ApproveGate';
import DemandBreakdownPanel from './DemandBreakdownPanel';
import { LAND_LABEL, STATUS_COLOR } from './status';
import { buildStory, coverDaysFrom, dayIndex } from './stockStory';
import StockStoryView, { type StoryMark } from './StockStoryView';

type Props = {
    /** Every part of every dip of the SKU, as the detail endpoint returns them. */
    rows: RequirementRow[] | null;
    /** OBJECTIVE: the rows are the dips of one objective's window, all shown. ALL (default): the SKU's horizon, later dips behind a toggle. */
    scope?: 'OBJECTIVE' | 'ALL';
    onClose: () => void;
    onApprove: (ids: string[]) => void;
    onSave?: (ids: string[]) => void;
    /** The objective's period: the curve spans it (and the landings that answer it), the demand is read over it. */
    period?: { start: string | null; end: string | null };
    /** Which tab opens first: the curve (default), the demand behind the DRR, or the chain of actions. */
    initialTab?: SkuTab;
};

export type SkuTab = 'story' | 'demand' | 'chain';

const n = (v: number, maxDecimals = 0): string => formatCount(v, { maxDecimals });

const STEP_VERB: Record<string, string> = { MATERIAL: 'Order', TRANSFER: 'Move', WORK_ORDER: 'Start', FG_PO: 'Order', SFG_ON_HAND: 'On hand' };

const Step: FC<{ c: ChainStepRow; index: number; today: string }> = ({ c, index, today }) => (
    <div className={classes.step}>
        <span className={classes.stepIndex}>{index + 1}</span>
        <div className={classes.lines}>
            <div className={classes.line}>
                {STEP_VERB[c.step]} {c.itemName ?? c.itemId} · {n(c.qty, c.unit ? 1 : 0)}
                {c.unit ? ` ${c.unit}` : ''}
            </div>
            <div className={classes.sub}>
                {c.step === 'MATERIAL' ? `need ${n(c.qty, 1)} · on floor ${n(c.available, 1)} · by transfer ${n(c.movable, 1)} · ${c.instrumentId ? `by order ${n(c.short, 1)}` : `short ${n(c.short, 1)}`}` : ''}
                {c.vendorName ? ` · ${c.vendorName}` : ''}
                {c.fromSiteId && c.toSiteId ? ` · ${c.fromSiteId} → ${c.toSiteId}` : ''}
                {c.leadDays != null ? ` · ${c.leadDays}d lead` : ''}
                {c.note ? ` · ${c.note}` : ''}
            </div>
        </div>
        <div className={classes.lines}>
            <div className={classes.line} data-late={c.placeBy && c.placeBy <= today && c.status === 'PROPOSED' ? 'true' : undefined}>
                {c.placeBy ? whenLabel(c.placeBy, today) : ''}
                {c.lands ? ` → lands ${dayLabel(c.lands)}` : ''}
            </div>
            {c.status && (
                <Badge size="xs" variant="light" color={STATUS_COLOR[c.status]} tt="none">
                    {c.status.toLowerCase()}
                </Badge>
            )}
        </div>
    </div>
);

/** One part of a dip's run (one, unless a buy is split across vendors): what it makes of what the curve asked for at its landing, its dates, what unlocked it, and its chain. */
const Part: FC<{ p: RequirementRow; today: string }> = ({ p, today }) => {
    const waits = waitsOnLabel(p.gatingMaterialId, p.chain);
    const land = LAND_LABEL[p.landStatus];
    const own = p.instruments.find((i) => i.id === p.instrumentId);
    const pinned = own?.pinned?.qty != null;
    const over = Number(own?.reason.overCapacity ?? 0);
    return (
        <Stack gap={2}>
            <div className={classes.partHead}>
                <span className={classes.partIndex}>
                    {p.parts > 1 ? `Part ${p.part} of ${p.parts}` : 'Run'}
                </span>
                <Text size="sm" fw={600}>
                    making {n(p.qty)} of {p.neededAtLanding != null ? n(p.neededAtLanding) : '—'} needed by {dayLabel(p.landDate)}
                    {pinned ? ' · fixed by hand' : ''}
                    {over > 0 ? ` · ${n(over)} above what the factory had free that day` : ''}
                </Text>
                <Text size="xs" c="dimmed">
                    {p.route === 'BUY' ? 'orders' : 'starts'} {whenLabel(p.startDate, today)} · lands {dayLabel(p.landDate)} · unlocked by {unlockLabel(p.unlockedBy, p.chain)}
                    {land ? ` · ${land.text}` : ''}
                    {p.stockoutDays > 0 ? ` · ${p.stockoutDays} days at zero before it lands` : ''}
                    {waits ? ` · short of ${waits}` : ''}
                </Text>
                {tiersLabel(p.tiers) && (
                    <Text size="xs" c="dimmed">
                        serves {tiersLabel(p.tiers)}
                    </Text>
                )}
            </div>
            {p.chain.map((c, i) => (
                <Step key={i} c={c} index={i} today={today} />
            ))}
        </Stack>
    );
};

const dipTitle = (d: Dip): string => (d.seq === 0 ? (d.head.landStatus === 'NO_DIP' ? 'Nothing to do' : 'Already covered') : d.seq === 1 ? 'Next replenishment' : `Later replenishment ${d.seq - 1}`);

/**
 * The lead the plan actually used: start → land of the next run (production) or order → land
 * (purchase). Not the worst case: that adds the slowest material vendor and only applies when
 * a material has to be ordered from nothing, which the chain shows when it happens.
 */
const leadText = (dips: Dip[]): string => {
    const p = dips.flatMap((d) => d.parts)[0];
    if (!p) return 'no run planned';
    const from = p.route === 'BUY' ? p.placeBy : p.startDate;
    const days = from && p.landDate ? Math.max(0, Math.round((new Date(`${p.landDate}T00:00:00`).getTime() - new Date(`${from}T00:00:00`).getTime()) / 86400000)) : null;
    return days == null ? 'lead —' : `${p.route === 'BUY' ? 'order lands in' : 'run lands in'} ${days}d`;
};

const landsText = (d: Dip): string => (d.parts.length > 1 && d.lastLand && d.lastLand !== d.firstLand ? `${dayLabel(d.firstLand)} → ${dayLabel(d.lastLand)}` : dayLabel(d.firstLand));

/** Lands-by sentence pieces, named by kind. */
const sumIn = (values: number[] | undefined, from: number, to: number): Array<{ i: number; qty: number }> => {
    const out: Array<{ i: number; qty: number }> = [];
    if (!values) return out;
    for (let i = from; i <= to && i < values.length; i += 1) if (values[i] > 0) out.push({ i, qty: values[i] });
    return out;
};

/**
 * The SKU's story under one objective: where its stock starts today, how demand pulls it
 * down, what is already coming (in production, in transit, approved) and when it lands,
 * where it dips below the level, and how the proposed quantity corrects the curve on the
 * earliest day it can land. Tabs: the curve, the demand behind the DRR, the chain of actions.
 */
const SkuWindow: FC<Props> = ({ rows, scope = 'ALL', onClose, onApprove, onSave, period, initialTab = 'story' }) => {
    const today = localDateKey();
    const [tab, setTab] = useState<SkuTab>(initialTab);
    const [showLater, setShowLater] = useState(false);
    useEffect(() => setTab(initialTab), [initialTab, rows]);
    const dips = useMemo(() => groupDips(rows ?? []), [rows]);
    const first = dips[0]?.head ?? null;
    // The curve rides on part 0 or 1 of the first dip; later parts carry none.
    const series = useMemo(() => (rows ?? []).find((r) => r.series)?.series ?? null, [rows]);
    // The window the plan answers. A run started on a day lands `lead` days later, so a plan for
    // 5 → 7 Nov with a one-day run answers demand on 6 → 8 Nov. The chart runs from the run date
    // to the end of that window and shades it; nothing past it.
    const story = useMemo(() => {
        if (!series || !first) return null;
        const shown = dips.filter((d) => scope === 'OBJECTIVE' || d.seq <= 1);
        const p1 = shown.flatMap((d) => d.parts)[0] ?? null;
        const startOf = p1 ? (p1.route === 'BUY' ? p1.placeBy : p1.startDate) : null;
        const lead = p1 && startOf && p1.landDate ? Math.max(0, dayIndex(startOf, p1.landDate) ?? 0) : 0;
        const pStart = period?.start ?? today;
        const pEnd = period?.end ?? shown.map((d) => d.lastLand).filter((d): d is string => Boolean(d)).sort().pop() ?? addDaysKey(today, 13);
        const window = { from: addDaysKey(pStart < today ? today : pStart, lead), to: addDaysKey(pEnd, lead), lead };
        const end = dayIndex(series.from, window.to) ?? 13;
        const days = Math.max(1, Math.min(series.demand.length, end + 1));
        const landings = shown.flatMap((d) => d.parts.map((p) => ({ day: p.landDate, qty: p.qty })));
        const built = buildStory({ series, onHand: first.onHand, levelDays: first.levelDays, refillDays: first.refillDays, landings, days });
        const marks: StoryMark[] = shown.flatMap((d) => [
            ...(d.head.dipDate ? [{ day: d.head.dipDate, kind: 'dip' as const }] : []),
            ...d.parts.flatMap((p) => [
                ...(p.route !== 'BUY' && p.startDate ? [{ day: p.startDate, kind: 'start' as const, qty: p.qty }] : []),
                ...(p.landDate ? [{ day: p.landDate, kind: 'land' as const, qty: p.qty }] : []),
            ]),
        ]);
        return { days: built, shown, window, marks };
    }, [series, first, dips, scope, period, today]);
    if (!first || !rows) return null;
    const open = [...new Set(dips.flatMap((d) => d.instruments.filter((i) => i.status === 'PROPOSED').map((i) => i.id)))];
    const later = scope === 'OBJECTIVE' ? 0 : dips.filter((d) => d.seq > 1).length;

    /** The curve, said in sentences: start, what is coming, the dip, and what the plan does about it. */
    const narrative = (): ReactElement | null => {
        if (!story || !series) return null;
        const last = story.days.length - 1;
        const levelUnits = story.days[0]?.level ?? 0;
        const production = sumIn(series.inProduction, 0, last);
        const transit = sumIn(series.inTransit, 0, last);
        const book = story.days.map((d, i) => ({ i, qty: d.onBook })).filter((x) => x.qty > 0);
        const lands = (xs: Array<{ i: number; qty: number }>): string =>
            xs
                .slice(0, 4)
                .map((x) => `${n(x.qty)} on ${dayLabel(addDaysKey(series.from, x.i))}`)
                .join(', ') + (xs.length > 4 ? ` +${xs.length - 4} more` : '');
        const items: ReactElement[] = [];
        items.push(
            <List.Item key="start">
                Starts today at <b>{n(first.onHand)}</b> units{first.coverDays != null ? `, ${n(first.coverDays)} days of cover` : ''}. Demand runs at about <b>{n(first.drr, 1)}</b> a day, so stock falls toward the level L ({first.levelDays} days ≈ {n(levelUnits)} units).
            </List.Item>,
        );
        if (production.length > 0) items.push(<List.Item key="prod">In production, landing: {lands(production)}.</List.Item>);
        if (transit.length > 0) items.push(<List.Item key="transit">In transit, landing: {lands(transit)}.</List.Item>);
        if (book.length > 0) items.push(<List.Item key="book">Approved and on the book, landing: {lands(book)}.</List.Item>);
        if (production.length + transit.length + book.length === 0) items.push(<List.Item key="none">Nothing is in production or in transit for it.</List.Item>);
        for (const d of story.shown) {
            const h = d.head;
            if (d.parts.length === 0) {
                if (h.landStatus === 'COVERED') items.push(<List.Item key={`c${d.key}`}>It would dip {dayLabel(h.dipDate)}, but a landing already on the book answers it on {dayLabel(h.landDate)}. Nothing new to place.</List.Item>);
                else if (h.landStatus === 'UNSOURCEABLE') items.push(<List.Item key={`u${d.key}`}>It dips {dayLabel(h.dipDate)} and has no factory, vendor or SFG route: nothing can be planned.</List.Item>);
                else if (h.landStatus === 'NO_DIP') items.push(<List.Item key={`n${d.key}`}>It stays above L for the whole period: nothing to place.</List.Item>);
                continue;
            }
            const made = d.parts.reduce((sum, p) => sum + p.qty, 0);
            const landI = dayIndex(series.from, d.firstLand);
            const after = landI != null && story.days[landI] ? story.days[landI].withPlan : null;
            const cover = landI != null && after != null ? coverDaysFrom(after, series.demand, landI) : null;
            const p1 = d.parts[0];
            const waits = waitsOnLabel(p1.gatingMaterialId, p1.chain);
            items.push(
                <List.Item key={`d${d.key}`}>
                    It dips below L on <b>{dayLabel(h.dipDate)}</b>
                    {h.zeroDate ? ` and would reach zero on ${dayLabel(h.zeroDate)}` : ''}. The earliest a new {p1.route === 'BUY' ? 'order' : 'run'} can land is <b>{dayLabel(d.firstLand)}</b>
                    {d.firstLand && h.dipDate && d.firstLand > h.dipDate ? `, ${Math.round(((dayIndex(h.dipDate, d.firstLand) ?? 0) * 10) / 10)} days after the dip` : ', on the dip'}. To refill to R ({h.refillDays} days) it is short <b>{n(h.requiredQty)}</b> there.
                    {p1.route !== 'BUY' && p1.startDate ? ` Production starts ${whenLabel(p1.startDate, today)}.` : ''} The plan lands <b>{n(made)}</b>
                    {made < h.requiredQty ? ` (the most that can be made by then${waits ? `: ${waits} is short` : ''})` : ''}
                    {after != null ? `, lifting stock to ${n(after)}${cover != null ? `, about ${n(cover)} days of cover` : ''}` : ''}.
                    {h.stockoutDays > 0 ? ` It is at zero for ${h.stockoutDays} days before that.` : ''}
                </List.Item>,
            );
        }
        return (
            <List size="sm" spacing={6}>
                {items}
            </List>
        );
    };

    const banner = (
        <div className={classes.banner}>
            <Group gap="lg" wrap="wrap">
                <Text size="sm">
                    On hand <b>{n(first.onHand)}</b>
                    {first.coverDays != null ? ` · ${n(first.coverDays)}d cover` : ''} · DRR <b>{n(first.drr, 1)}</b>/day
                </Text>
                <Text size="sm">
                    Level <b>{first.levelDays}d</b> · refill to <b>{first.refillDays}d</b> · {leadText(dips)}
                </Text>
                <Text size="sm">
                    Dips <b>{whenLabel(first.dipDate, today)}</b> · zero <b>{dayLabel(first.zeroDate)}</b> · lands <b>{landsText(dips[0])}</b>
                    {first.stockoutDays > 0 ? ` · ${first.stockoutDays} days at zero` : ''}
                </Text>
            </Group>
        </div>
    );

    const dipBlock = (d: Dip): ReactElement => {
        const h = d.head;
        return (
            <Stack key={d.key} gap={4}>
                <Group gap="xs">
                    <Text fw={700} size="sm">
                        {dipTitle(d)}
                    </Text>
                    <Badge size="xs" variant="light" tt="none">
                        {h.landStatus.replaceAll('_', ' ').toLowerCase()}
                    </Badge>
                    <Text size="xs" c="dimmed">
                        dips {dayLabel(h.dipDate)} · lands {landsText(d)} · {n(h.requiredQty)} units
                        {d.parts.length > 1 ? ` in ${d.parts.length} parts` : ''}
                    </Text>
                </Group>
                {d.parts.length === 0 && (
                    <Text size="sm" c="dimmed">
                        {h.landStatus === 'COVERED' ? `Handled: a landing already on the book (approved or in transit) answers this dip on ${dayLabel(h.landDate)}. Nothing new to order.${h.stockoutDays > 0 ? ` ${h.stockoutDays} days of stockout until then.` : ''}` : h.landStatus === 'UNSOURCEABLE' ? 'No factory, vendor or SFG route is mapped for this SKU.' : h.landStatus === 'NO_DIP' ? (h.dipDate ? `Nothing to plan: the first dip is ${dayLabel(h.dipDate)}, past the furthest window an objective asks about. ${n(h.onHand)} on hand at ${n(h.drr, 1)} a day${h.coverDays != null ? `, ${n(h.coverDays)} days of cover` : ''}.` : `Nothing to plan: the SKU never dips below its ${h.levelDays}-day level inside the horizon. ${n(h.onHand)} on hand at ${n(h.drr, 1)} a day${h.coverDays != null ? `, ${n(h.coverDays)} days of cover` : ''}.`) : 'Nothing to do.'}
                    </Text>
                )}
                {d.parts.map((p) => (
                    <Part key={p.id} p={p} today={today} />
                ))}
            </Stack>
        );
    };

    return (
        <ExplainWindow
            opened
            wide
            onClose={onClose}
            title={first.sku}
            subtitle={first.productName}
            banner={banner}
            footer={
                open.length > 0 ? (
                    <Group justify="flex-end" p="sm">
                        {onSave && (
                            <Button size="xs" variant="subtle" color="grape" title="Keep the chain's numbers as they stand, without creating any document" onClick={() => onSave(open)}>
                                Save
                            </Button>
                        )}
                        <ApproveGate>
                            <Button size="xs" onClick={() => onApprove(open)}>
                                Approve the whole chain ({open.length})
                            </Button>
                        </ApproveGate>
                    </Group>
                ) : undefined
            }
        >
            <Tabs value={tab} onChange={(v) => setTab((v as SkuTab) ?? 'story')} px="md" pt="sm">
                <Tabs.List>
                    <Tabs.Tab value="story">Stock story</Tabs.Tab>
                    <Tabs.Tab value="demand">Demand</Tabs.Tab>
                    <Tabs.Tab value="chain">Actions and chain</Tabs.Tab>
                </Tabs.List>
                <Tabs.Panel value="story" pt="md" pb="md">
                    <Stack gap="md">
                        {narrative()}
                        {story && story.days.length > 0 ? (
                            <StockStoryView days={story.days} window={story.window} period={period} marks={story.marks} levelDays={first.levelDays} refillDays={first.refillDays} />
                        ) : (
                            <Text size="sm" c="dimmed">
                                No curve stored for this SKU on the latest run.
                            </Text>
                        )}
                        {series && !series.inTransit && (
                            <Text size="xs" c="dimmed">
                                In production and in transit are shown apart from the next run on; this run stored them together, drawn as approved.
                            </Text>
                        )}
                    </Stack>
                </Tabs.Panel>
                <Tabs.Panel value="demand" pt="md" pb="md">
                    <DemandBreakdownPanel productId={first.productId} from={today} to={period?.end ?? null} />
                </Tabs.Panel>
                <Tabs.Panel value="chain" pt="md" pb="md">
                    <Stack gap="md">
                        {dips.filter((d) => scope === 'OBJECTIVE' || d.seq <= 1 || showLater).map(dipBlock)}
                        {later > 0 && (
                            <Button size="compact-xs" variant="subtle" onClick={() => setShowLater((v) => !v)}>
                                {showLater ? 'Hide later replenishments' : `${later} later replenishment${later === 1 ? '' : 's'} on the horizon, each sized after the previous one lands · show`}
                            </Button>
                        )}
                    </Stack>
                </Tabs.Panel>
            </Tabs>
        </ExplainWindow>
    );
};

export default SkuWindow;
